import { useSeo } from '../lib/seo';
/**
 * Town detail — richer layout with AI-generated seasonal inspiration,
 * attractions, accommodation, and a county map. Designed to feel like a
 * mini magazine spread per market town.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowUpRight, BedDouble, Footprints, MapPin, Sparkles, UtensilsCrossed } from 'lucide-react';
import api from '../lib/api';
import AttractionCard from '../components/AttractionCard';
import CountyMap from '../components/CountyMap';

// Split text into clean paragraphs: real paragraph breaks (\n\n) become
// separate <p>; stray single newlines (legacy 70-char wraps) collapse to spaces.
function toParagraphs(text) {
  if (!text) return [];
  return text
    .replace(/\r\n/g, '\n')
    .split(/\n\s*\n+/)
    .map((p) => p.replace(/\s*\n\s*/g, ' ').trim())
    .filter(Boolean);
}

// Official local destination sites — linked quietly at the foot of each town page.
// A slug can map to more than one site (e.g. Blaby also shows Visit Harborough,
// which covers Market Harborough and Lutterworth next door).
// Hinckley & Market Bosworth destination films — built into the page so they
// show on every environment regardless of database contents.
const TOWN_VIDEOS = {
  'hinckley-market-bosworth': {
    label: 'See it for yourself',
    title: 'Walks on the Bosworth 1485 Trail',
    intro: "Take a few minutes to stroll the Bosworth 1485 Trail with us. These short films follow two of the area's loveliest walks, winding through the quiet countryside around Market Bosworth — the very landscape where the Wars of the Roses reached their dramatic close in 1485, now marked by striking sculptures along the way.\n\nThey're easy, rewarding routes for families and seasoned walkers alike, and just two of many footpaths that thread between Hinckley, Market Bosworth and the villages in between. Watch, then come and walk them yourself.",
    films: [
      { url: '/api/video/goleics/public_videos/sculpture-trail-walk-one.mp4', caption: 'Bosworth 1485 Trail, walk one — Calm before the storm' },
      { url: '/api/video/goleics/public_videos/bosworth-trail-walk.mp4', caption: 'Bosworth 1485 Trail, walk two' },
    ],
    infoUrl: 'https://www.richardiiicountry.com/richard-iii/walking-the-trail',
    infoLabel: 'Trail routes & parking',
  },
};

// Area-specific walking pages on gps-routes.co.uk — frontend fallback so the
// right district page is linked even if the town record has no walks field.
const TOWN_WALKS = {
  'hinckley-market-bosworth': { url: 'https://www.gps-routes.co.uk/routes/home.nsf/RoutesLinksWalks/hinckley-walking-route', area: 'Hinckley & Market Bosworth' },
  'loughborough-charnwood': { url: 'https://www.gps-routes.co.uk/routes/home.nsf/RoutesLinksWalks/charnwood-forest-walking-route', area: 'Charnwood Forest' },
  'melton-mowbray': { url: 'https://www.gps-routes.co.uk/routes/home.nsf/routeslinkswalks/melton-country-park-walking-and-cycle-route', area: 'Melton Mowbray' },
  'leicester': { url: 'https://www.gps-routes.co.uk/routes/home.nsf/RoutesLinksWalks/river-soar-walking-route', area: 'Leicester' },
  'ashby-de-la-zouch-coalville': { url: 'https://www.gps-routes.co.uk/routes/home.nsf/RoutesLinksWalks/coalville-walking-route', area: 'Ashby & Coalville' },
  'blaby-district': { url: 'https://www.gps-routes.co.uk/routes/home.nsf/RoutesLinksWalks/market-harborough-canal-walking-route', area: 'Market Harborough & south Leicestershire' },
};

const DESTINATION_SITES = {
  leicester: [{ name: 'Visit Leicester', url: 'https://visitleicester.info/' }],
  'loughborough-charnwood': [{ name: 'Discover Charnwood', url: 'https://www.discovercharnwood.co.uk/' }],
  'melton-mowbray': [{ name: 'Discover Melton', url: 'https://discovermelton.com/' }],
  'hinckley-market-bosworth': [{ name: 'Richard III Country', url: 'https://www.richardiiicountry.com/' }],
  'blaby-district': [
    { name: 'Visit Harborough', url: 'https://visitharborough.com/' },
    { name: 'Visit Blaby', url: 'https://www.visitblaby.org.uk/' },
  ],
  'ashby-de-la-zouch-coalville': [{ name: 'Visit North West Leicestershire', url: 'https://visitnwleics.com/visit/' }],
  'market-harborough': [{ name: 'Visit Harborough', url: 'https://visitharborough.com/' }],
  lutterworth: [{ name: 'Visit Harborough', url: 'https://visitharborough.com/' }],
};

// Answers real questions people type into Google before visiting each area.
// Rendered at the foot of every town page AND emitted as FAQPage schema.
const TOWN_FAQS = {
  leicester: [
    {
      q: 'Is Leicester worth visiting?',
      a: "Absolutely. Leicester packs a lot into one walkable city: the King Richard III Visitor Centre (built over the very spot the king was found), his tomb in Leicester Cathedral, the UK's National Space Centre, one of England's oldest street markets, and the Golden Mile's legendary food. It's an easy, affordable day out — and a great base for exploring the county's market towns.",
    },
    {
      q: 'What is Leicester famous for?',
      a: "Three things above all: the astonishing 2012 discovery of King Richard III under a city car park, Leicester City's fairy-tale Premier League title in 2016, and the National Space Centre — the UK's largest attraction dedicated to space. Add the Golden Mile's Diwali celebrations (among the biggest outside India) and the county's famous foods, from Melton Mowbray pork pies to Red Leicester cheese.",
    },
    {
      q: 'What are the best things to do in Leicester with kids?',
      a: "Start with the National Space Centre — real rockets, a planetarium and interactive galleries that keep families busy for hours. Leicester Museum & Art Gallery on New Walk has dinosaur skeletons and is free to enter, Abbey Park has boating, play areas and miniature railways in season, and football families can tour the King Power Stadium.",
    },
    {
      q: 'How do I get to Leicester?',
      a: "Leicester sits right in the middle of England. By car it's just off the M1 (junction 21) and M69. By rail, direct trains run from London St Pancras in about 1 hour 15 minutes, and from Birmingham in around 50 minutes. East Midlands Airport is roughly 30 minutes away.",
    },
    {
      q: 'How long should I spend in Leicester?',
      a: "A full day covers the city's headline sights comfortably. Make it a weekend and you can add the county too — Bradgate Park's deer and ruins, Bosworth Battlefield, the National Forest, and market towns like Melton Mowbray and Market Harborough are all within half an hour.",
    },
  ],
  'melton-mowbray': [
    {
      q: 'What is Melton Mowbray famous for?',
      a: "Food, above everything. Melton Mowbray is the Rural Capital of Food — the only place in the world allowed to make the authentic Melton Mowbray pork pie, and one of the historic homes of Stilton cheese. Add one of England's oldest street markets and a packed calendar of food festivals and you can see why food lovers make the pilgrimage.",
    },
    {
      q: 'Is Melton Mowbray worth visiting?',
      a: "Very much so. Beyond the famous pies and cheese there's a proper working market town to enjoy: independent shops, historic St Mary's church, Melton Country Park, and Belvoir Castle's spectacular grounds just up the road. It's an easy, satisfying day out — especially on a market day.",
    },
    {
      q: 'When is market day in Melton Mowbray?',
      a: "Markets run several days a week, with Tuesday the big one — it's home to one of the country's last surviving town-centre livestock markets, a genuine slice of rural England. Saturdays are lively too, with stalls across the town centre.",
    },
    {
      q: 'What can families do in Melton Mowbray?',
      a: "Twinlakes theme park on the edge of town is one of the county's favourite family days out, Melton Country Park has trails and play areas, and older kids enjoy watching the pork pies being made. Belvoir Castle's adventure playground and gardens are a short drive away.",
    },
    {
      q: 'How do I get to Melton Mowbray?',
      a: "Melton is in the north-east of Leicestershire, about 30 minutes from Leicester by car or rail. Direct trains connect Melton Mowbray with Leicester, Peterborough and beyond, and the A606/A607 link it to Nottingham, Grantham and Leicester.",
    },
  ],
  'loughborough-charnwood': [
    {
      q: 'What is Loughborough famous for?',
      a: "Bells, trains and sport. Loughborough is home to Taylor's Bell Foundry — the largest working bell foundry in the world — and the Great Central Railway, the UK's only double-track mainline heritage railway. Its world-famous university has produced generations of Olympians, and the Carillon tower rings out over Queen's Park.",
    },
    {
      q: 'Is Charnwood worth visiting?',
      a: "Charnwood is Leicestershire's wild side — ancient volcanic crags, heathland and woodland just minutes from the M1. Bradgate Park (with its deer herds and the ruins of Lady Jane Grey's home) and Beacon Hill are two of the county's best-loved outdoor spaces, and the villages between them are full of good pubs.",
    },
    {
      q: 'What can families do in Loughborough and Charnwood?',
      a: "Ride behind a full-size steam engine on the Great Central Railway, meet the deer at Bradgate Park, climb Old John for the county's best view, or let off steam in Queen's Park. Rainy day? The Charnwood Museum is free and right in the middle of town.",
    },
    {
      q: 'Where are the best walks in Charnwood?',
      a: "Bradgate Park and Beacon Hill are the classics — proper hills by Midlands standards, with rocks that are among the oldest in England. The Outwoods, Swithland Wood and Charnwood's quiet lanes offer gentler wanders, all within a few miles of Loughborough.",
    },
    {
      q: 'How do I get to Loughborough?',
      a: "Loughborough sits on the Midland Main Line, with fast trains to Leicester, Nottingham, Derby and London St Pancras (around 90 minutes). By car it's just off the M1 (junctions 23 and 23a) and the A6.",
    },
  ],
  'hinckley-market-bosworth': [
    {
      q: 'What is the Battle of Bosworth and where did it happen?',
      a: "Bosworth 1485 is where the Wars of the Roses ended — King Richard III was killed in battle and Henry Tudor took the crown, changing English history. The Bosworth Battlefield Heritage Centre, near Market Bosworth, tells the story brilliantly, with the actual battlefield walks alongside.",
    },
    {
      q: 'Is Market Bosworth worth visiting?',
      a: "Yes — it's one of Leicestershire's prettiest market towns, with a fine Georgian square, independent shops and a proper unhurried feel. Combine it with the Battlefield Heritage Centre, a cruise or towpath walk on the Ashby Canal, or a ride on the Battlefield Line heritage railway.",
    },
    {
      q: 'What is Hinckley famous for?',
      a: "Motorbikes, for a start — Hinckley is the global home of Triumph Motorcycles, whose factory visitor experience is a must for anyone with petrol in their veins. The town also has a lively market heritage going back over 700 years and makes a handy base for the Bosworth area.",
    },
    {
      q: 'What can families do around Hinckley and Market Bosworth?',
      a: "The Battlefield Heritage Centre brings history to life with trails, events and falconry days, and the Battlefield Line heritage railway runs steam and diesel trains through the countryside. Add a boat trip or towpath wander on the Ashby Canal — and Twycross Zoo is only a short drive away.",
    },
    {
      q: 'How do I get to Hinckley and Market Bosworth?',
      a: "Hinckley is just off the A5 and M69 with direct trains to Leicester, Nuneaton and Birmingham. Market Bosworth is about 15 minutes north by car. From Leicester, allow around half an hour to either.",
    },
  ],
  'ashby-de-la-zouch-coalville': [
    {
      q: 'What is Ashby de la Zouch famous for?',
      a: "Its castle, above all — the dramatic ruins of Ashby de la Zouch Castle, immortalised in Sir Walter Scott's Ivanhoe. Climb the Hastings Tower for huge views, then wander a handsome Georgian high street that's full of independent shops and places to eat.",
    },
    {
      q: 'What is there to do in the National Forest?',
      a: "This corner of Leicestershire sits at the heart of the National Forest — millions of trees transforming former mining country. Conkers is the flagship family attraction with indoor and outdoor discovery zones, Hicks Lodge is the forest's cycling centre, and Moira Furnace adds a fascinating slice of industrial heritage.",
    },
    {
      q: 'Is Coalville worth visiting?',
      a: "Coalville is the gateway to the National Forest and proud of its mining roots. It makes a practical base for Conkers, Hicks Lodge and forest trails, and heritage lovers should look out for events celebrating the area's industrial past.",
    },
    {
      q: 'What can families do around Ashby and Coalville?',
      a: "Conkers keeps kids busy for a full day, rain or shine. Add castle battlements to storm at Ashby, bike trails for all ages at Hicks Lodge, and canal-side walks at Moira Furnace. It's one of the county's best areas for an active family weekend.",
    },
    {
      q: 'How do I get to Ashby de la Zouch?',
      a: "Ashby sits just off the A42/M42 in the north-west of the county, about 30–40 minutes from Leicester, Derby and Birmingham by car, with East Midlands Airport only 15 minutes away.",
    },
  ],
  'blaby-district': [
    {
      q: 'What is Market Harborough famous for?',
      a: "A gorgeous Georgian town centre and one of the county's most famous landmarks nearby: Foxton Locks, the spectacular staircase of ten canal locks on the Grand Union Canal. In town, look for the Old Grammar School — a timber building on wooden stilts that's stood since 1614.",
    },
    {
      q: 'What is Lutterworth known for?',
      a: "Two world-changers: John Wycliffe, who made the first English translation of the Bible while rector here in the 1380s, and Sir Frank Whittle, who developed the jet engine in Lutterworth in the 1940s. It's a charming small market town with a big place in history.",
    },
    {
      q: 'Is Foxton Locks worth visiting?',
      a: "Definitely — it's one of the great sights of England's canal network and a brilliant free day out. Watch boats work their way up the famous staircase, walk the towpaths, visit the little museum, and finish with a drink at the waterside pubs.",
    },
    {
      q: 'What can families do around Market Harborough, Lutterworth and Blaby?',
      a: "Foxton Locks is the family favourite — boats, ducks, ice cream and space to run. Add country parks and trails across Blaby district, canal-side cycling on the Grand Union, and market days in Harborough town centre for a proper day of it.",
    },
    {
      q: 'How do I get to Market Harborough?',
      a: "Market Harborough is on the Midland Main Line with direct trains to London St Pancras in about an hour — one of the quickest routes into the county. Lutterworth sits just off the M1 (junction 20), and Blaby is minutes from Leicester's southern edge.",
    },
  ],
};

export default function TownDetail() {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  const isLeicester = slug === 'leicester';
  const seoTitle = t
    ? (isLeicester
        ? 'Visit Leicester — Things to Do, Days Out & City Guide | GoLeicestershire'
        : `Visit ${t.name.split('&')[0].trim()} — Days Out, Places to Eat & Stay | GoLeicestershire`)
    : null;
  const seoDesc = t
    ? (isLeicester
        ? "Planning to visit Leicester? Your local guide to the city — the King Richard III story, the National Space Centre, the Golden Mile, one of England's oldest markets, family days out, and the best places to eat and stay."
        : `Visit ${t.name.split('&')[0].trim()}: things to do, places to eat and places to stay in ${t.name}, Leicestershire — with local tips, events and days out.`)
    : null;
  useSeo(seoTitle, seoDesc);
  const [insp, setInsp] = useState(null);
  const [inspLoading, setInspLoading] = useState(false);
  const [faqExtras, setFaqExtras] = useState([]);

  useEffect(() => {
    api.get('/site-settings/town_faq_extras')
      .then((r) => setFaqExtras((r.data?.value?.items || []).filter((x) => x.town_slug === slug && x.q && x.a)))
      .catch(() => {});
  }, [slug]);
  const townFaqs = [...(TOWN_FAQS[slug] || []), ...faqExtras];

  useEffect(() => {
    setT(null); setInsp(null);
    api.get(`/towns/${slug}`).then((r) => setT(r.data));
    setInspLoading(true);
    api.get(`/towns/${slug}/inspiration`).then((r) => setInsp(r.data)).finally(() => setInspLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (!t) return <div className="py-32 text-center text-muted">Loading...</div>;
  const shortName = t.name.split('&')[0].trim();
  const attractions = t.attractions || [];
  const accommodation = t.accommodation || [];
  const foodDrink = t.food_drink || [];

  return (
    <div data-testid={`town-detail-${slug}`}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'TouristDestination',
          name: t.name,
          description: seoDesc,
          url: `https://goleicestershire.com/towns/${slug}`,
          ...(t.hero_image ? { image: t.hero_image } : {}),
          touristType: ['Families', 'Couples', 'Day trippers'],
          containedInPlace: { '@type': 'AdministrativeArea', name: 'Leicestershire', containedInPlace: { '@type': 'Country', name: 'United Kingdom' } },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://goleicestershire.com/' },
            { '@type': 'ListItem', position: 2, name: 'Towns', item: 'https://goleicestershire.com/towns' },
            { '@type': 'ListItem', position: 3, name: t.name, item: `https://goleicestershire.com/towns/${slug}` },
          ],
        })}</script>
        {townFaqs.length > 0 && (
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: townFaqs.map((f) => ({
              '@type': 'Question', name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          })}</script>
        )}
      </Helmet>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-8">
        <Link to="/towns" className="inline-flex items-center gap-2 text-muted hover:text-heritage text-sm" data-testid="town-back"><ArrowLeft size={16}/> All towns</Link>
      </div>

      {/* HERO */}
      <div className="relative aspect-[16/8] lg:aspect-[16/6] mt-4 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="relative w-full h-full rounded-[2rem] overflow-hidden grain">
          <img src={t.hero_image} alt={t.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6 md:p-12 lg:p-16 text-base">
            <div className="label text-mustard mb-3">{isLeicester ? 'The city' : 'Market town'}</div>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-8xl leading-[0.9]">Visit {t.name}</h1>
            <div className="mt-4 flex flex-wrap gap-2 text-sm">
              {attractions.length > 0 && <span className="bg-base/15 backdrop-blur px-3 py-1.5 rounded-full">{attractions.length} things to do</span>}
              {foodDrink.length > 0 && <span className="bg-base/15 backdrop-blur px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"><UtensilsCrossed size={12}/> {foodDrink.length} places to eat</span>}
              {accommodation.length > 0 && <span className="bg-base/15 backdrop-blur px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"><BedDouble size={12}/> {accommodation.length} places to stay</span>}
            </div>
          </div>
        </div>
      </div>

      {/* AI INSPIRATION + DESCRIPTION */}
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
        <aside className="lg:col-span-2">
          <div className="bg-charcoal text-base rounded-3xl p-6 md:p-8 grain relative overflow-hidden">
            <div className="label text-mustard mb-3 inline-flex items-center gap-2"><Sparkles size={14}/> Why visit {insp?.for_day === 'tomorrow' ? 'tomorrow' : 'today'}</div>
            {inspLoading && <div className="text-base/60 text-sm py-4">Loading…</div>}
            {!inspLoading && insp && (
              <>
                <div className="space-y-3 text-base/90 leading-relaxed text-[15px] md:text-base" data-testid="town-inspiration-copy">
                  {toParagraphs(insp.copy).map((p, i) => <p key={i}>{p}</p>)}
                </div>
                {(insp.season || insp.weather) && (
                  <div className="mt-5 pt-5 border-t border-base/15">
                    <span className="text-base/50 text-xs uppercase tracking-widest">{insp.season}{insp.weather ? ` · ${insp.weather}` : ''}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </aside>
        <div className="lg:col-span-3 text-charcoal/85">
          <div className="label text-heritage mb-3">About {shortName}</div>
          {(t.description || t.summary) && (
            <div className="space-y-4 text-lg lg:text-xl leading-relaxed" data-testid="town-description">
              {toParagraphs(t.description || t.summary).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          )}
        </div>
      </div>

      {/* LOCAL WALKS — small data-driven link to the area's routes on gps-routes.co.uk */}
      {(() => {
        const walks = t.walks || TOWN_WALKS[t.slug] || null;
        return (
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-10" data-testid="town-walks-link-section">
        <a
          href={walks?.url || 'https://www.gps-routes.co.uk/routes/home.nsf/county-walks/Leicestershire'}
          target="_blank" rel="noopener noreferrer"
          className="group inline-flex flex-wrap items-center gap-3 bg-stone/50 border border-charcoal/10 rounded-2xl px-5 py-4 hover:border-heritage/40 hover:bg-stone/70 transition-colors"
          data-testid="town-walks-link"
        >
          <Footprints size={18} className="text-heritage shrink-0" />
          <span className="text-charcoal/80 text-sm">
            <strong className="text-charcoal">Walking around {walks?.area || shortName}:</strong>{' '}
            find local routes, maps and trail notes on gps-routes.co.uk
          </span>
          <span className="text-heritage font-semibold text-sm inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
            View walks <ArrowUpRight size={13} />
          </span>
        </a>
        <p className="text-charcoal/40 text-xs mt-2">
          External site — routes are published and maintained by gps-routes.co.uk. Please check conditions before setting out.
        </p>
      </section>
        );
      })()}

      {/* DESTINATION FILMS — built-in config per town */}
      {(() => {
        const video = TOWN_VIDEOS[t.slug];
        if (!video?.films?.length) return null;
        return (
        <section className="bg-charcoal py-14 md:py-20" data-testid="town-video-section">
          <div className="max-w-[1250px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="text-base lg:col-span-2">
              <div className="label text-mustard mb-3">{video.label || 'See it for yourself'}</div>
              <h2 className="font-display text-3xl lg:text-4xl leading-tight mb-5">{video.title}</h2>
              <div className="space-y-3 text-base/85 leading-relaxed" data-testid="town-video-intro">
                {toParagraphs(video.intro || '').map((p, i) => <p key={i}>{p}</p>)}
              </div>
              {video.infoUrl && (
                <div className="mt-6">
                  <a href={video.infoUrl} target="_blank" rel="noopener noreferrer" data-testid="town-video-info-link" data-outbound="website"
                    className="inline-flex items-center gap-2 bg-mustard text-charcoal font-semibold px-6 py-3 rounded-full hover:bg-base transition-colors text-sm">
                    {video.infoLabel || 'Plan your walk'} <ArrowUpRight size={15} />
                  </a>
                  <p className="text-base/50 text-xs mt-2">External site — official trail information by Richard III Country.</p>
                </div>
              )}
            </div>
            <div className="lg:col-span-3 flex flex-wrap justify-center gap-8">
              {video.films.map((f, i) => (
                <figure key={f.url} className="flex flex-col items-center">
                  <video controls playsInline preload="metadata"
                    className="rounded-2xl shadow-xl h-[420px] md:h-[480px] aspect-[9/16] max-w-full bg-black"
                    src={`${process.env.REACT_APP_BACKEND_URL}${f.url}`}
                    data-testid={`town-video-player-${i + 1}`} />
                  {f.caption && (
                    <figcaption className="text-base/70 text-sm mt-3" data-testid={`town-video-caption-${i + 1}`}>{f.caption}</figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        </section>
        );
      })()}

      {/* ATTRACTIONS */}
      {attractions.length > 0 && (
        <section className="bg-stone/40 py-14 md:py-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8 md:mb-10">
              <h2 className="font-display text-3xl lg:text-5xl leading-tight">Days out in {shortName}</h2>
              <Link to={`/explore?town=${encodeURIComponent(shortName)}`} className="text-heritage hover:text-charcoal font-semibold text-sm inline-flex items-center gap-1.5">See all in /explore →</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {attractions.slice(0, 8).map((a) => <AttractionCard key={a.id} a={a}/>)}
            </div>
            <div className="mt-10 md:mt-12">
              <CountyMap items={attractions} height={400} />
            </div>
          </div>
        </section>
      )}

      {/* EAT & DRINK */}
      {foodDrink.length > 0 && (
        <section className="bg-white py-14 md:py-20" data-testid="town-food-drink">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8 md:mb-10">
              <div>
                <div className="label text-heritage mb-2 inline-flex items-center gap-2"><UtensilsCrossed size={14}/> Eat & drink</div>
                <h2 className="font-display text-3xl lg:text-4xl leading-tight">Places to eat in {shortName}</h2>
              </div>
              <Link to={`/explore?category=food_drink&town=${encodeURIComponent(shortName)}`} className="text-heritage hover:text-charcoal font-semibold text-sm inline-flex items-center gap-1.5">All eat & drink →</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {foodDrink.slice(0, 8).map((a) => <AttractionCard key={a.id} a={a}/>)}
            </div>
          </div>
        </section>
      )}

      {/* ACCOMMODATION */}
      {accommodation.length > 0 && (
        <section className="py-14 md:py-20" data-testid="town-accommodation">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8 md:mb-10">
              <div>
                <div className="label text-heritage mb-2 inline-flex items-center gap-2"><BedDouble size={14}/> Stay over</div>
                <h2 className="font-display text-3xl lg:text-4xl leading-tight">Places to stay near {shortName}</h2>
              </div>
              <Link to="/accommodation" className="text-heritage hover:text-charcoal font-semibold text-sm inline-flex items-center gap-1.5">All accommodation →</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {accommodation.slice(0, 4).map((a) => (
                <Link key={a.id} to={`/explore/${a.slug}`} className="group bg-white border border-charcoal/10 rounded-3xl overflow-hidden hover:shadow-soft hover:-translate-y-1 transition-all block">
                  <div className="aspect-[4/3] overflow-hidden bg-stone">{a.hero_image && <img src={a.hero_image} alt={a.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>}</div>
                  <div className="p-5">
                    <h3 className="font-display text-lg leading-tight mb-1 group-hover:text-heritage transition-colors">{a.name}</h3>
                    <span className="text-heritage font-semibold text-xs inline-flex items-center gap-1">See stay <ArrowUpRight size={11}/></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TOWN FAQ — bottom of page, visible answers matching the FAQPage schema */}
      {townFaqs.length > 0 && (
        <section className="bg-white py-14 md:py-20" data-testid="town-faq">
          <div className="max-w-[900px] mx-auto px-6 lg:px-12">
            <div className="label text-heritage mb-3">Planning your visit</div>
            <h2 className="font-display text-3xl lg:text-4xl leading-tight mb-8">Visiting {shortName} — your questions, answered.</h2>
            <div className="space-y-4">
              {townFaqs.map((f, i) => (
                <details key={i} className="group bg-base border border-charcoal/10 rounded-2xl px-6 py-5" data-testid={`town-faq-${i}`}>
                  <summary className="font-display text-lg cursor-pointer list-none flex items-center justify-between gap-4">
                    {f.q}
                    <span className="text-heritage text-xl leading-none group-open:rotate-45 transition-transform shrink-0">+</span>
                  </summary>
                  <p className="text-charcoal/75 leading-relaxed mt-3 text-[15px]">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Official local destination sites — deliberately low-key, open in a new tab */}
      {DESTINATION_SITES[slug] && (
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 pb-14 pt-4 text-sm text-muted" data-testid="destination-site-link">
          Looking for more local information? See the area&apos;s own visitor site{DESTINATION_SITES[slug].length > 1 ? 's' : ''}:{' '}
          {DESTINATION_SITES[slug].map((site, i) => (
            <React.Fragment key={site.url}>
              {i > 0 && ' and '}
              <a href={site.url} target="_blank" rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-heritage transition-colors"
                data-testid={`destination-site-${slug}-${i}`}>
                {site.name}
              </a>
            </React.Fragment>
          ))}.
        </div>
      )}
    </div>
  );
}
